import { useQuery } from '@tanstack/react-query'
import {
  getOepDocumentProcessingStatus,
  type OepDocumentProcessingStatus,
  type OepDocumentProcessingStatusDto,
} from 'modules/annual-benefit-planning/modules/approval/infra/api/config.api'
import { APPROVAL_QUERY_KEYS } from 'modules/annual-benefit-planning/modules/approval/infra/react-query/approval.query-keys'

const POLL_INTERVAL_MS = 15_000
const MAX_FAILED_POLLS = 8

const TERMINAL_STATUSES: ReadonlySet<
  OepDocumentProcessingStatusDto['processingStatus']
> = new Set<OepDocumentProcessingStatusDto['processingStatus']>([
  'completed',
  'failed',
])

/**
 * Stops once we have confirmed a terminal status ('completed' or 'failed') —
 * polls every 15s in every other case (still processing, no data yet, or a
 * transient load error), so a single flaky request doesn't permanently freeze
 * the poll. A 'failed' job is not retried automatically (retry stays a manual
 * /abp/dashboard action), so there is nothing further to wait for once it's
 * reached. Also stops after MAX_FAILED_POLLS failed requests, so a status
 * endpoint that keeps failing is not polled for as long as the page is open.
 */
export function resolveRefetchInterval(
  result: OepDocumentProcessingStatusDto | undefined,
  failedPollCount = 0
): number | false {
  if (failedPollCount >= MAX_FAILED_POLLS) return false
  return result !== undefined && TERMINAL_STATUSES.has(result.processingStatus)
    ? false
    : POLL_INTERVAL_MS
}

interface Options {
  readonly clientId: string
  readonly documentId: string
  /** Set false for a row that will never read the result (e.g. already Applied) so it doesn't poll forever for a value nobody uses. Defaults to true. */
  readonly enabled?: boolean
}

interface Result {
  readonly processingStatus: OepDocumentProcessingStatus | undefined
  readonly isLoading: boolean
}

/**
 * Per-document poll of the analysis file's processing status (EMS-10185 User
 * Story 2) — backs the second (contribution model) Download icon's gating in
 * OepXlsxUploadSection.tsx (User Story 3). Polls every 15s while 'processing',
 * stops once 'completed' or 'failed'. Mirrors useOepTargetFileJobs's
 * refetchInterval-as-function pattern.
 */
export function useOepDocumentProcessingStatus({
  clientId,
  documentId,
  enabled = true,
}: Options): Result {
  const { data, isLoading } = useQuery({
    queryKey: APPROVAL_QUERY_KEYS.oepDocumentProcessingStatus(
      clientId,
      documentId
    ),
    queryFn: () => getOepDocumentProcessingStatus(clientId, documentId),
    enabled: enabled && clientId !== '' && documentId !== '',
    refetchInterval: (query) =>
      resolveRefetchInterval(query.state.data, query.state.errorUpdateCount),
  })

  return { processingStatus: data?.processingStatus, isLoading }
}
